import {
  DataRefreshEvent,
  type BatchProcessCreatedPayload,
} from "@fp/admin-api-client";
import { ChevronDown, Loading02, X } from "@untitledui/icons";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useBatchProcesses } from "./batch-process-context";
import { BatchProcessItemList } from "./batch-process-item-list";
import {
  getUndecidedBatches,
  getVisibleWidgetBatches,
} from "./batch-process-status";
import { BatchProcessSummary } from "./batch-process-summary";
import { useBatchProcessRun } from "./use-batch-process-run";
import { useDataRefresh } from "@/hooks/useSignalR";
import { isOverlayOpen } from "@/lib/is-overlay-open";

function WidgetShell({
  isExpanded,
  onToggleExpanded,
  onDismiss,
  children,
}: {
  isExpanded?: boolean;
  onToggleExpanded?: () => void;
  onDismiss: () => void;
  children: ReactNode;
}) {
  return (
    <div className="fixed left-1/2 top-4 z-50 w-[min(28rem,calc(100vw-2rem))] -translate-x-1/2">
      <div className="relative overflow-hidden rounded-xl border border-secondary bg-primary shadow-xl ring-1 ring-black/5">
        <div className="absolute right-2 top-2 flex items-center gap-1">
          {onToggleExpanded && (
            <button
              type="button"
              onClick={onToggleExpanded}
              aria-label={isExpanded ? "Collapse" : "Expand"}
              aria-expanded={isExpanded}
              className="rounded-md p-1 text-fg-quaternary hover:bg-primary_hover hover:text-fg-quaternary_hover"
            >
              <ChevronDown
                className={`size-4 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
              />
            </button>
          )}
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss"
            className="rounded-md p-1 text-fg-quaternary hover:bg-primary_hover hover:text-fg-quaternary_hover"
          >
            <X className="size-4" />
          </button>
        </div>

        {children}
      </div>
    </div>
  );
}

export function BatchProcessLiveWidget() {
  const {
    batches,
    canView,
    pendingBatch,
    clearPendingBatch,
    isClaimedByCurrentView,
  } = useBatchProcesses();
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [presentedIds, setPresentedIds] = useState<Set<string>>(new Set());
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const decidedIds = useRef<Set<string>>(new Set());

  useDataRefresh<BatchProcessCreatedPayload>(
    DataRefreshEvent.BatchProcessCreated,
    (payload) => {
      setPresentedIds((prev) => {
        if (prev.has(payload.batchProcessId)) {
          return prev;
        }
        return new Set(prev).add(payload.batchProcessId);
      });
    },
  );

  useEffect(() => {
    const undecided = getUndecidedBatches(
      batches,
      presentedIds,
      decidedIds.current,
    );

    if (undecided.length === 0) {
      return;
    }

    const claimedIds = undecided
      .filter(isClaimedByCurrentView)
      .map((batch) => batch.id);

    for (const batch of undecided) {
      decidedIds.current.add(batch.id);
    }

    if (claimedIds.length === 0) {
      return;
    }

    setExpandedIds((prev) => {
      const next = new Set(prev);

      for (const id of claimedIds) {
        next.add(id);
      }

      return next;
    });
  }, [batches, presentedIds, isClaimedByCurrentView]);

  const visibleBatches = canView
    ? getVisibleWidgetBatches(batches, presentedIds, dismissedIds)
    : [];
  const current =
    visibleBatches.find((batch) => !batch.isComplete) ?? visibleBatches[0];
  const isExpanded = current ? expandedIds.has(current.id) : false;
  const run = useBatchProcessRun(current ?? null, isExpanded);

  const dismiss = () => {
    if (current) {
      setDismissedIds((ids) => new Set(ids).add(current.id));
      return;
    }

    clearPendingBatch();
  };

  useHotkeys(
    "escape",
    () => {
      if (!isOverlayOpen()) {
        dismiss();
      }
    },
    { enabled: !!current || !!pendingBatch },
    [current, pendingBatch],
  );

  if (!current) {
    if (!pendingBatch || !canView) {
      return null;
    }

    return (
      <WidgetShell onDismiss={clearPendingBatch}>
        <div className="p-4">
          <BatchProcessSummary batch={pendingBatch} />
        </div>
        <div className="flex items-center justify-center gap-2 border-t border-secondary bg-secondary_alt px-4 py-8 text-sm text-tertiary">
          <Loading02 className="size-4 animate-spin" />
          Discovering…
        </div>
      </WidgetShell>
    );
  }

  const toggleExpanded = () => {
    setExpandedIds((ids) => {
      const next = new Set(ids);
      if (next.has(current.id)) {
        next.delete(current.id);
      } else {
        next.add(current.id);
      }
      return next;
    });
  };

  const additional = visibleBatches.length - 1;

  return (
    <WidgetShell
      isExpanded={isExpanded}
      onToggleExpanded={toggleExpanded}
      onDismiss={dismiss}
    >
      <div className="p-4">
        <BatchProcessSummary batch={run.batch ?? current} />

        {additional > 0 && (
          <p className="mt-2 text-xs text-tertiary">
            +{additional} more — open the drawer to see all
          </p>
        )}
      </div>

      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
      >
        <div className="overflow-hidden">
          <div className="max-h-96 overflow-y-auto">
            <BatchProcessItemList run={run} />
          </div>
        </div>
      </div>
    </WidgetShell>
  );
}